import React, { useState } from 'react';
import { Shield, ArrowLeft, CheckCircle, Award, FileText, Globe, Briefcase, Clock, Zap } from 'lucide-react';

const VisionTechCertificates = () => {
  const [currentView, setCurrentView] = useState('main');
  const [animateCertificate, setAnimateCertificate] = useState(false);

  const certificates = [
    {
      id: 'incorporation',
      title: 'Certificate of Incorporation',
      image: '/certificate.PNG',
      icon: <Briefcase size={24} />,
      description: 'Official business registration certificate',
      fullTitle: 'Official Business Certificate',
      verificationStatus: 'Verified & Authenticated',
      companySection: {
        title: 'Vision Tech 2030',
        description: [
          'Vision Tech 2030 is a Silicon Valley-based technology company revolutionizing industries through advanced technological solutions. This year, we expanded to establish a hub that drives innovation and creates meaningful impact in the technology landscape.',
          'Our platform empowers businesses with scalable, efficient tools while offering unique opportunities for investment in cutting-edge technology products. By joining us, you become part of a mission to shape the future with sustainable and intelligent innovations.',
          'We welcome forward-thinking individuals to our dynamic team, where creativity and collaboration continuously redefine progress. With Vision Tech 2030, we\'re transforming communities, industries, and the investment landscape. Join us on this journey to drive innovation and sustainable growth!'
        ],
        compliancePoints: [
          'Registered Business Entity',
          'Verified Corporate Structure',
          'Compliant Technology Operations'
        ]
      }
    },
    
    {
      id: 'clientmoney',
      title: 'Client Money Protection',
      image: '/client_money_protection.PNG',
      icon: <Shield size={24} />,
      description: 'Financial security certification',
      fullTitle: 'Guardian of Prosperity',
      verificationStatus: 'Trust Certified',
      companySection: {
        title: 'Vision Tech\'s Promise',
        description: [
          'At Vision Tech 2030, we prioritize the security and protection of client funds above all else. Our Client Money Protection certification ensures that all investments are handled with the utmost care and diligence.',
          'Your funds are safeguarded through industry-leading security protocols and regular compliance audits. We maintain strict segregation of client funds from operational accounts, ensuring your investments remain protected under all circumstances.',
          'Our transparent financial practices and regular reporting mechanisms provide you with complete visibility into your investments, building trust through accountability and proven results.'
        ],
        compliancePoints: [
          'Secure Fund Management',
          'Independent Auditor Verification',
          'Regulatory Compliance Excellence'
        ]
      }
    }
  ];

  const handleViewCertificate = (id) => {
    setAnimateCertificate(true);
    setTimeout(() => {
      setCurrentView(id);
      setAnimateCertificate(false);
    }, 300);
  };

  const renderMainView = () => (
    <div className="vtech-certificates-main">
      <div className="vtech-certificates-header">
        <div className="vtech-certificates-header-icon">
          <Award size={28} />
        </div>
        <h2 className="vtech-certificates-title">Our Certifications</h2>
        <p className="vtech-certificates-subtitle">
          Official documents validating Vision Tech 2030's compliance and excellence in various domains
        </p>
      </div>

      <div className="vtech-certificates-grid">
        {certificates.map((cert) => (
          <div 
            key={cert.id} 
            className="vtech-certificate-card"
            onClick={() => handleViewCertificate(cert.id)}
          >
            <div className="vtech-certificate-card-inner">
              <div className="vtech-certificate-icon-container">
                <div className="vtech-certificate-icon">
                  {cert.icon}
                </div>
              </div>
              
              <h3 className="vtech-certificate-card-title">{cert.title}</h3>
              <p className="vtech-certificate-card-description">{cert.description}</p>
              
              <div className="vtech-certificate-preview">
                <img 
                  src={cert.image} 
                  alt={cert.title} 
                  className="vtech-preview-image"
                />
                <div className="vtech-certificate-preview-overlay">
                  <span className="vtech-view-certificate">View Certificate</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCertificateView = () => {
    const certificate = certificates.find(cert => cert.id === currentView);
    
    if (!certificate) return null;
    
    return (
      <div className="vtech-certificate-detail-view">
        <div className="vtech-certificate-detail-header">
          <button 
            className="vtech-back-button" 
            onClick={() => setCurrentView('main')}
          >
            <ArrowLeft size={20} />
            <span>Back to Certificates</span>
          </button>
          <div className="vtech-certificate-detail-title-container">
            <h3 className="vtech-certificate-detail-title">{certificate.title}</h3>
            <div className="vtech-certificate-verification">
              <CheckCircle size={16} className="vtech-verification-icon" />
              <span>{certificate.verificationStatus}</span>
            </div>
          </div>
        </div>
        
        <div className="vtech-certificate-detail-content">
          <div className="vtech-certificate-showcase">
            <div className="vtech-certificate-showcase-card">
              <div className="vtech-certificate-showcase-header">
                <div className="vtech-certificate-icon-large">
                  {certificate.icon}
                </div>
                <h4 className="vtech-certificate-full-title">{certificate.fullTitle}</h4>
              </div>
              
              <div className="vtech-certificate-image-container">
                <img 
                  src={certificate.image}
                  alt={certificate.title}
                  className="vtech-certificate-full-image"
                />
              </div>
              
              <div className="vtech-certificate-badge">
                <div className="vtech-certificate-badge-icon">
                  <CheckCircle size={20} />
                </div>
                <span className="vtech-certificate-badge-text">{certificate.verificationStatus}</span>
              </div>
            </div>
          </div>
          
          <div className="vtech-certificate-description">
            <div className="vtech-description-header">
              <h4 className="vtech-description-title">{certificate.companySection.title}</h4>
              <div className="vtech-description-divider"></div>
            </div>
            
            <div className="vtech-description-content">
              {certificate.companySection.description.map((paragraph, index) => (
                <p key={index} className="vtech-description-paragraph">{paragraph}</p>
              ))}
              
              <div className="vtech-compliance-points">
                {certificate.companySection.compliancePoints.map((point, index) => (
                  <div key={index} className="vtech-compliance-item">
                    <CheckCircle size={18} className="vtech-compliance-icon" />
                    <span className="vtech-compliance-text">{point}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="vtech-certificate-verification-seal">
              <div className="vtech-seal-inner">
                <Zap size={28} className="vtech-seal-icon" />
                <span className="vtech-seal-text">Vision Tech 2030</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`vtech-certificates-container ${animateCertificate ? 'vtech-animate-transition' : ''}`}>
      {currentView === 'main' ? renderMainView() : renderCertificateView()}
      
      <style jsx>{`
        .vtech-certificates-container {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem 1.5rem;
          color: #1f2937;
          transition: opacity 0.3s ease;
        }
        
        .vtech-animate-transition {
          opacity: 0.5;
        }
        
        /* Main View Styles */
        .vtech-certificates-header {
          text-align: center;
          margin-bottom: 3rem;
        }
        
        .vtech-certificates-header-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, #4f46e5, #7c3aed);
          color: white;
          border-radius: 16px;
          margin-bottom: 1.5rem;
        }
        
        .vtech-certificates-title {
          font-size: 2.5rem;
          font-weight: 800;
          color: #111827;
          margin-bottom: 1rem;
          letter-spacing: -0.025em;
        }
        
        .vtech-certificates-subtitle {
          font-size: 1.125rem;
          color: #4b5563;
          max-width: 600px;
          margin: 0 auto;
        }
        
        .vtech-certificates-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1.5rem;
        }
        
        .vtech-certificate-card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
          cursor: pointer;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          height: 100%;
          border: 1px solid rgba(0, 0, 0, 0.05);
        }
        
        .vtech-certificate-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
        }
        
        .vtech-certificate-card-inner {
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        
        .vtech-certificate-icon-container {
          margin-bottom: 1.25rem;
        }
        
        .vtech-certificate-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #4f46e5, #7c3aed);
          color: white;
          border-radius: 12px;
        }
        
        .vtech-certificate-card-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #111827;
          margin: 0 0 0.5rem 0;
        }
        
        .vtech-certificate-card-description {
          font-size: 0.95rem;
          color: #4b5563;
          margin: 0 0 1.5rem 0;
        }
        
        .vtech-certificate-preview {
          position: relative;
          border-radius: 8px;
          overflow: hidden;
          margin-top: auto;
          border: 1px solid rgba(0, 0, 0, 0.08);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          height: 150px;
          transition: transform 0.3s ease;
        }
        
        .vtech-certificate-card:hover .vtech-certificate-preview {
          transform: scale(1.02);
        }
        
        .vtech-preview-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: top;
        }
        
        .vtech-certificate-preview-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        .vtech-certificate-card:hover .vtech-certificate-preview-overlay {
          opacity: 1;
        }
        
        .vtech-view-certificate {
          background: white;
          color: #4f46e5;
          font-size: 0.875rem;
          font-weight: 600;
          padding: 0.5rem 1rem;
          border-radius: 20px;
        }
        
        /* Certificate Detail View Styles */
        .vtech-certificate-detail-view {
          animation: vtech-fade-in 0.4s ease;
        }
        
        @keyframes vtech-fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .vtech-certificate-detail-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }
        
        .vtech-back-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: #f3f4f6;
          border: none;
          padding: 0.625rem 1.25rem;
          border-radius: 6px;
          font-weight: 500;
          font-size: 0.95rem;
          color: #4b5563;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .vtech-back-button:hover {
          background: #e5e7eb;
          color: #1f2937;
        }
        
        .vtech-certificate-detail-title-container {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        
        .vtech-certificate-detail-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #111827;
          margin: 0;
        }
        
        .vtech-certificate-verification {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(16, 185, 129, 0.1);
          color: #065f46;
          padding: 0.375rem 0.75rem;
          border-radius: 20px;
          font-size: 0.875rem;
          font-weight: 500;
        }
        
        .vtech-verification-icon {
          color: #10b981;
        }
        
        .vtech-certificate-detail-content {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }
        
        @media (min-width: 992px) {
          .vtech-certificate-detail-content {
            grid-template-columns: minmax(300px, 400px) 1fr;
          }
        }
        
        .vtech-certificate-showcase {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        
        .vtech-certificate-showcase-card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          position: relative;
          border: 1px solid rgba(0, 0, 0, 0.05);
        }
        
        .vtech-certificate-showcase-header {
          padding: 1.5rem;
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        
        .vtech-certificate-icon-large {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #4f46e5, #7c3aed);
          color: white;
          border-radius: 12px;
        }
        
        .vtech-certificate-full-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #111827;
          margin: 0;
        }
        
        .vtech-certificate-image-container {
          padding: 1.5rem;
          display: flex;
          justify-content: center;
          align-items: center;
          background: #f9fafb;
        }
        
        .vtech-certificate-full-image {
          max-width: 100%;
          height: auto;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(0, 0, 0, 0.1);
        }
        
        .vtech-certificate-badge {
          position: absolute;
          bottom: 1.5rem;
          right: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(79, 70, 229, 0.9);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.875rem;
          font-weight: 500;
          backdrop-filter: blur(4px);
        }
        
        .vtech-certificate-badge-icon {
          color: white;
        }
        
        .vtech-certificate-validity {
          background: white;
          border-radius: 12px;
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          border: 1px solid rgba(0, 0, 0, 0.05);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }
        
        .vtech-validity-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem;
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }
        
        .vtech-validity-item:last-child {
          border-bottom: none;
        }
        
        .vtech-validity-item svg {
          color: #4f46e5;
        }
        
        .vtech-validity-text {
          display: flex;
          flex-direction: column;
        }
        
        .vtech-validity-label {
          font-size: 0.875rem;
          color: #6b7280;
        }
        
        .vtech-validity-value {
          font-size: 1rem;
          font-weight: 500;
          color: #111827;
        }
        
        .vtech-certificate-description {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
          border: 1px solid rgba(0, 0, 0, 0.05);
          position: relative;
        }
        
        .vtech-description-header {
          margin-bottom: 1.5rem;
        }
        
        .vtech-description-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #111827;
          margin: 0 0 0.75rem 0;
        }
        
        .vtech-description-divider {
          width: 80px;
          height: 4px;
          background: linear-gradient(90deg, #4f46e5, #7c3aed);
          border-radius: 2px;
        }
        
        .vtech-description-content {
          color: #4b5563;
        }
        
        .vtech-description-paragraph {
          font-size: 1rem;
          line-height: 1.7;
          margin-bottom: 1.25rem;
        }
        
        .vtech-compliance-points {
          margin-top: 2rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        
        .vtech-compliance-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          background: #f9fafb;
          padding: 0.75rem 1rem;
          border-radius: 8px;
        }
        
        .vtech-compliance-icon {
          color: #10b981;
        }
        
        .vtech-compliance-text {
          font-weight: 500;
          color: #111827;
        }
        
        .vtech-certificate-verification-seal {
          position: absolute;
          top: 2rem;
          right: 2rem;
          width: 80px;
          height: 80px;
        }
        
        .vtech-seal-inner {
          position: relative;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background: rgba(79, 70, 229, 0.05);
          border: 2px dashed #4f46e5;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 0.5rem;
        }
        
        .vtech-seal-icon {
          color: #4f46e5;
          margin-bottom: 0.25rem;
        }
        
        .vtech-seal-text {
          font-size: 0.625rem;
          font-weight: 600;
          color: #4f46e5;
          text-align: center;
          line-height: 1.2;
        }
        
        /* Responsive Adjustments */
        @media (max-width: 768px) {
          .vtech-certificates-title {
            font-size: 2rem;
          }
          
          .vtech-certificates-grid {
            grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          }
          
          .vtech-certificate-detail-header {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .vtech-certificate-detail-title-container {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }
          
          .vtech-certificate-verification-seal {
            position: relative;
            top: auto;
            right: auto;
            margin-top: 2rem;
            margin-left: auto;
            margin-right: auto;
          }
        }
      `}</style>
    </div>
  );
};

export default VisionTechCertificates;
